import React from "react";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { Link } from "react-router-dom";
import { ROUTES } from "src/routes/RouteConfig";

import {
  BookOpen,
  Clock,
  Users,
  Target,
  CheckCircle,
  ArrowRight,
  Play,
  Lock,
  Award
} from "lucide-react";

// Common Core Math Curriculum by Grade
const mathCurriculum = [
  {
    grade: "Kindergarten",
    level: 1,
    status: "available",
    color: "bg-green-500",
    topics: [
      "Counting and Cardinality",
      "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten",
      "Measurement and Data",
      "Geometry"
    ],
    description: "Build foundational math skills through counting, basic addition/subtraction, and shape recognition.",
    duration: "Full Year Program",
    syllabusUrl: null // To be added later
  },
  {
    grade: "Grade 1",
    level: 2,
    status: "available",
    color: "bg-blue-500",
    topics: [
      "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten",
      "Measurement and Data",
      "Geometry"
    ],
    description: "Develop addition and subtraction strategies, place value understanding, and measurement concepts.",
    duration: "Full Year Program",
    syllabusUrl: null // To be added later
  },
  {
    grade: "Grade 2",
    level: 3,
    status: "available",
    color: "bg-purple-500",
    topics: [
      "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten",
      "Measurement and Data",
      "Geometry"
    ],
    description: "Master two-digit addition/subtraction, understand place value to 1000, and work with time and money.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/EXCIR5xpT_REpCHsIKnqq94BoIDp2Ql0oDION1A-HOh0ug?e=uNiw8b"
  },
  {
    grade: "Grade 3",
    level: 4,
    status: "available",
    color: "bg-orange-500",
    topics: [
      "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten",
      "Number and Operations—Fractions",
      "Measurement and Data",
      "Geometry"
    ],
    description: "Introduction to multiplication, division, and fractions with area and perimeter concepts.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/Ec1MCmWdgKNGsbJh8Jmrf-4BN0Zgr1aE4fKSsAkljIu2sA?e=lKTCUY"
  },
  {
    grade: "Grade 4",
    level: 5,
    status: "available",
    color: "bg-red-500",
    topics: [
      "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten",
      "Number and Operations—Fractions",
      "Measurement and Data",
      "Geometry"
    ],
    description: "Multi-digit arithmetic, equivalent fractions, and decimal notation.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/EV9N1VFclhZBmKw6MB_rC8ABUcA59pdEVqnwWcZspKp1kA?e=jij3pB"
  },
  {
    grade: "Grade 5",
    level: 6,
    status: "available",
    color: "bg-indigo-500",
    topics: [
      "Operations and Algebraic Thinking",
      "Number and Operations in Base Ten",
      "Number and Operations—Fractions",
      "Measurement and Data",
      "Geometry"
    ],
    description: "Decimal operations, fraction operations, and coordinate geometry.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/Edzrxdek6fNGov03gETPkMoBVtpXmiVolWnLcugD6AGJkA?e=IApHyK"
  },
  {
    grade: "Grade 6",
    level: 7,
    status: "available",
    color: "bg-teal-500",
    topics: [
      "Ratios and Proportional Relationships",
      "The Number System",
      "Expressions and Equations",
      "Geometry",
      "Statistics and Probability"
    ],
    description: "Introduction to ratios, negative numbers, algebraic expressions, and statistical thinking.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/ETDgutsYbZ5JrblVbDYGYVgBdRsaGjY4UW_Mk990JJkP4A?e=GcOWj0"
  },
  {
    grade: "Grade 7",
    level: 8,
    status: "available",
    color: "bg-cyan-500",
    topics: [
      "Ratios and Proportional Relationships",
      "The Number System",
      "Expressions and Equations",
      "Geometry",
      "Statistics and Probability"
    ],
    description: "Proportional relationships, rational numbers, linear equations, and geometric constructions.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/ERFQq9A7gv9NsmCs_KzNwgkBtiIXaUTuHhssZ7tOc62LoA?e=2fGyVo"
  },
  {
    grade: "Grade 8",
    level: 9,
    status: "available",
    color: "bg-emerald-500",
    topics: [
      "The Number System",
      "Expressions and Equations",
      "Functions",
      "Geometry",
      "Statistics and Probability"
    ],
    description: "Introduction to functions, linear equations, geometric transformations, and data analysis.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/Eferw5SIXABHqxNDDeVDwTEBGJQSbV1xCLaWaxFmZruxgg?e=Mk7imv"
  },
  {
    grade: "Algebra 1",
    level: 10,
    status: "available",
    color: "bg-amber-500",
    topics: [
      "Seeing Structure in Expressions",
      "Arithmetic with Polynomials and Rational Expressions",
      "Creating Equations",
      "Reasoning with Equations and Inequalities",
      "Linear, Quadratic, and Exponential Models"
    ],
    description: "Foundation of algebraic thinking with linear and quadratic functions, systems of equations.",
    duration: "Full Year Program",
    syllabusUrl: null // To be added later
  },
  {
    grade: "Geometry",
    level: 11,
    status: "available",
    color: "bg-rose-500",
    topics: [
      "Congruence",
      "Similarity, Right Triangles, and Trigonometry",
      "Circles",
      "Expressing Geometric Properties with Equations",
      "Geometric Measurement and Dimension"
    ],
    description: "Formal geometric proofs, coordinate geometry, trigonometry, and spatial reasoning.",
    duration: "Full Year Program",
    syllabusUrl: null // To be added later
  },
  {
    grade: "Algebra 2",
    level: 12,
    status: "available",
    color: "bg-violet-500",
    topics: [
      "Polynomial, Rational, and Radical Relationships",
      "Exponential and Logarithmic Relationships",
      "Trigonometric Functions",
      "Mathematical Modeling",
      "Statistics and Probability"
    ],
    description: "Advanced algebraic concepts, exponential and logarithmic functions, trigonometry.",
    duration: "Full Year Program",
    syllabusUrl: null // To be added later
  },
  {
    grade: "Precalculus",
    level: 13,
    status: "available",
    color: "bg-fuchsia-500",
    topics: [
      "Advanced Functions",
      "Trigonometry and Complex Numbers",
      "Polynomial and Rational Functions",
      "Exponential and Logarithmic Functions",
      "Sequences, Series, and Limits"
    ],
    description: "Preparation for calculus with advanced functions, trigonometry, and mathematical analysis.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/EW7F0IZ06SRHhup5nMPm9ZUBBn73En-E65IHeClk2DvjmQ?e=F3BE5U"
  },
    {
    grade: "Calculus BC",
    level: 14,
    status: "available",
    color: "bg-fuchsia-500",
    topics: [
      "Functions and Graphs",
      "Derivatives and Integrals",
      "Inverse and Transcendental Functions",
      "Limits and Continuity",
      "Rates of Change",
      "Separable Differential Equations and Slope Field",
      "Sequences and Series"
    ],
    description: "Differentiation, integration, limits, analysis of functions, and real-world applications.",
    duration: "Full Year Program",
    syllabusUrl: "https://farantal.sharepoint.com/:b:/s/ReinforcementLearningSolutions/EWcUnL-ihBZLuQ4weyFu3tQBCAuN3pHpjIL7D9hn5bNrOQ?e=k7zGXN"
  }
];

const Courses = () => {
  // Helper function to get count of available grades
  const getAvailableGradesCount = () => {
    return mathCurriculum.filter(grade => grade.status === 'available').length;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow overflow-hidden py-8 sm:py-12">
        <section className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="mb-20 grid items-center gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:gap-20">
            <div className="lg:py-8">
              <h1 className="max-w-[10ch] text-5xl font-extrabold leading-[0.98] tracking-[-0.04em] text-gray-950 sm:text-6xl lg:text-7xl">
                Find the next thing that clicks.
              </h1>
              <p className="mt-7 max-w-xl text-lg leading-8 text-gray-600 sm:text-xl">
                Personalized math courses built around your learner&apos;s starting point—then guided, one meaningful skill at a time.
              </p>
              <p className="mt-5 max-w-lg text-base leading-7 text-gray-500">
                Our {getAvailableGradesCount()}-course Common Core sequence begins in Kindergarten and continues through Calculus BC.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button size="lg" className="button-gradient text-white" asChild>
                  <Link to={ROUTES.REGISTER.path}>
                    Start with their grade
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-gray-300 bg-white text-gray-800 hover:bg-rfpurple-50 hover:text-rfpurple-800" asChild>
                  <Link to={ROUTES.BOOK_DEMO.path}>
                    <Users className="mr-2 h-5 w-5" />
                    Find the right fit
                  </Link>
                </Button>
              </div>
            </div>

            <div className="relative border-y border-rfpurple-200 bg-rfpurple-50 px-6 py-7 sm:px-8 sm:py-9">
              <div className="absolute -left-px top-8 bottom-8 w-1 bg-rfpurple-600" aria-hidden="true" />
              <p className="pl-4 text-sm font-semibold text-rfpurple-800">A course path with room to grow</p>
              <div className="mt-7 divide-y divide-rfpurple-200">
                <div className="grid grid-cols-[5.5rem_1fr] gap-4 py-5 first:pt-0">
                  <span className="text-sm font-bold text-rfpurple-700">K – 5</span>
                  <div><p className="font-bold text-gray-900">Build number confidence</p><p className="mt-1 text-sm leading-6 text-gray-600">Counting, operations, fractions, measurement, and problem-solving.</p></div>
                </div>
                <div className="grid grid-cols-[5.5rem_1fr] gap-4 py-5">
                  <span className="text-sm font-bold text-rfpurple-700">6 – 8</span>
                  <div><p className="font-bold text-gray-900">Connect ideas and reasoning</p><p className="mt-1 text-sm leading-6 text-gray-600">Ratios, expressions, equations, geometry, and data.</p></div>
                </div>
                <div className="grid grid-cols-[5.5rem_1fr] gap-4 pb-0 pt-5">
                  <span className="text-sm font-bold text-rfpurple-700">9 – 12</span>
                  <div><p className="font-bold text-gray-900">Prepare for what&apos;s next</p><p className="mt-1 text-sm leading-6 text-gray-600">Algebra, geometry, precalculus, and Calculus BC.</p></div>
                </div>
              </div>
            </div>
          </div>

          {/* Math Curriculum Journey */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Common Core Math Curriculum Journey
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Follow our structured learning path designed to build mathematical foundations
                progressively from Kindergarten through High School graduation.
              </p>
            </div>

            {/* Grade Cards Grid Layout */}
            <div className="relative max-w-6xl mx-auto">
              {/* Grade Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-48 md:max-w-5xl mx-auto">
                {mathCurriculum.map((grade, index) => (
                  <div key={grade.grade} className="relative md:max-w-lg mx-auto w-full">
                    {/* Top-right icon per card by index: Artboard1.svg -> ArtboardN.svg (visible on mobile too) */}
                    <div className="absolute -top-12 -right-32" style={{ zIndex: 1 }}>
                      <img
                        src={`/course-card-icon/Artboard${index + 1}.svg`}
                        alt="Decorative icon"
                        className="w-56 h-56 opacity-70"
                      />
                    </div>

                    {/* Bottom-left remaining icons distributed to cards 0..N */}
                    {mathCurriculum.length + index + 1 <= 16 && (
                      <div className="absolute -bottom-12 -left-28 hidden md:block" style={{ zIndex: 1 }}>
                        <img
                          src={`/course-card-icon/Artboard${mathCurriculum.length + index + 1}.svg`}
                          alt="Decorative icon"
                          className="w-56 h-56 opacity-60"
                        />
                      </div>
                    )}

                    <Card className={`${grade.status === 'available' ? 'bg-white shadow-lg hover:shadow-xl' : 'bg-gray-50 opacity-75'} transition-all duration-300 flex flex-col min-h-[470px] relative z-20`}>
                      <CardHeader className="pb-3 flex-shrink-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`w-8 h-8 rounded-full ${grade.color} flex items-center justify-center`}>
                              {grade.status === 'available' ? (
                                <Play className="w-4 h-4 text-white" />
                              ) : (
                                <Lock className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <CardTitle className="text-xl font-bold">{grade.grade}</CardTitle>
                          </div>
                          <Badge
                            variant={grade.status === 'available' ? 'default' : 'secondary'}
                            className={grade.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}
                          >
                            {grade.status === 'available' ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Available
                              </>
                            ) : (
                              <>
                                <Clock className="w-3 h-3 mr-1" />
                                Coming Soon
                              </>
                            )}
                          </Badge>
                        </div>
                        <CardDescription className="text-gray-600">
                          {grade.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-1 p-6 pt-0">
                        <div className="mb-4 flex-1">
                          <h4 className="font-semibold text-sm text-gray-900 mb-2">Core Standards:</h4>
                          <div className="space-y-1">
                            {grade.topics.map((topic, topicIndex) => (
                              <div key={topicIndex} className="flex items-center text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 bg-rfpurple-400 rounded-full mr-2"></div>
                                {topic}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-auto pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between mb-4">
                            <div className="text-sm text-gray-500 flex items-center">
                              <Clock size={14} className="mr-1" />
                              {grade.duration}
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <Award size={14} className="mr-1" />
                              Level {grade.level}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              className={`flex-1 ${grade.status === 'available' ? 'button-gradient text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                              disabled={grade.status !== 'available'}
                              asChild={grade.status === 'available'}
                            >
                              {grade.status === 'available' ? (
                                <Link to="/register">
                                  Start Learning
                                  <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                              ) : (
                                <span>
                                  Coming Soon
                                  <Lock className="ml-2 h-4 w-4" />
                                </span>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              className={`flex-1 border-gray-300 text-gray-600 hover:bg-gray-50 ${!grade.syllabusUrl ? 'cursor-not-allowed opacity-50' : ''}`}
                              asChild={grade.syllabusUrl !== null}
                              disabled={!grade.syllabusUrl}
                            >
                              {grade.syllabusUrl ? (
                                <a 
                                  href={grade.syllabusUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Download Syllabus
                                </a>
                              ) : (
                                <span>
                                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Coming Soon
                                </span>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-rfpurple-600 to-rfpurple-500 rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Your Learning Journey?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Begin with our personalized mathematics curriculum and stay tuned for more exciting courses
              coming your way with expert tutors and interactive learning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="bg-white text-rfpurple-600 hover:bg-gray-100" asChild>
                <Link to="/register">
                  <Target className="mr-2 h-5 w-5" />
                  Get Started Now
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-rfpurple-600 bg-transparent" asChild>
                <Link to="/inquiry">
                  <BookOpen className="mr-2 h-5 w-5" />
                  Learn More
                </Link>
              </Button>
            </div>
          </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Courses;
